import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";

export interface Assessment {
  id: number;
  title: string;
  description: string;
  type: string;
  module: string;
  duration: number;
  total_marks: number;
  pass_marks: number;
  due_date: string | null;
  instructions: string;
  resources: string;
  status: string;
  created_by: number;
  institution_id: number;
  created_at: string;
  updated_at: string;
  questions?: AssessmentQuestion[];
}

export interface AssessmentQuestion {
  id: number;
  assessment_id: number;
  question_text: string;
  question_type: string;
  options: string;
  correct_answer: string;
  marks: number;
  order: number;
}

export interface AssessmentSubmission {
  id?: number;
  assessment_id: number;
  student_id: number;
  answers: string;
  score?: number;
  percentage?: number;
  status: string;
  submitted_at: string;
}

// Get all assessments
export function useAssessments(institutionId?: number) {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["assessments", institutionId],
    queryFn: async () => {
      if (user?.isDemo) return [];
      const params = institutionId ? { institution_id: institutionId } : {};
      const { data } = await apiClient.get("/assessments", { params });
      return (data as any)?.data || data;
    },
  });
}

// Get single assessment with questions
export function useAssessment(id: number) {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ["assessment", id],
    queryFn: async () => {
      if (user?.isDemo) return null;
      const { data } = await apiClient.get(`/assessments/${id}`);
      return (data as any)?.data || data;
    },
    enabled: !!id,
  });
}

// Create assessment
export function useCreateAssessment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (assessment: Partial<Assessment>) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), ...assessment };
      }
      
      const { data } = await apiClient.post("/assessments", assessment);
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

// Update assessment
export function useUpdateAssessment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async ({ id, ...assessment }: { id: number } & Partial<Assessment>) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id, ...assessment };
      }
      
      const { data } = await apiClient.put(`/assessments/${id}`, assessment);
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

// Delete assessment
export function useDeleteAssessment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (id: number) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { success: true };
      }
      
      const { data } = await apiClient.delete(`/assessments/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

// Submit assessment
export function useSubmitAssessment() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async (submission: AssessmentSubmission) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id: Date.now(), ...submission };
      }
      
      const { data } = await apiClient.post(`/assessments/${submission.assessment_id}/submit`, submission);
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

// Grade submission
export function useGradeSubmission() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  return useMutation({
    mutationFn: async ({ id, score, percentage, feedback }: { id: number; score: number; percentage: number; feedback: string }) => {
      if (user?.isDemo) {
        await new Promise((r) => setTimeout(r, 500));
        return { id, score, percentage, feedback, status: "graded" };
      }
      
      const { data } = await apiClient.put(`/assessments/submissions/${id}/grade`, { score, percentage, feedback });
      return (data as any)?.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}
